import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { useSession } from "next-auth/react"
import {
  checkCalendarConnection,
  checkGmailConnection,
} from "@/app/helpers/database/users/checkGoogleConnections"
import { getUserSlackIntegrations } from "@/app/helpers/slack/userSlackClient"

interface GlobalContextProps {
  isConnectedToSlack: boolean
  isConnectedToGmail: boolean
  isConnectedToCalendar: boolean
  checkUserSlackConnection: () => Promise<void>
  checkUserGmailConnection: () => Promise<void>
  checkUserCalendarConnection: () => Promise<void>
  isLoadingSlackConnections: boolean
  isLoadingGmailConnections: boolean
  isLoadingCalendarConnections: boolean
  checkAllUserConnections: () => Promise<void>
  loadingUserConnections: boolean
}

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined)

interface GlobalProviderProps {
  children: ReactNode
}

export const UserContext: React.FC<GlobalProviderProps> = ({ children }) => {
  const [isConnectedToSlack, setIsConnectedToSlack] = useState(false)
  const [isConnectedToGmail, setIsConnectedToGmail] = useState(false)
  const [isConnectedToCalendar, setIsConnectedToCalendar] = useState(false)
  // Start with false to avoid showing loading states on initial render
  // They'll be set to true when we actually start fetching
  const [isLoadingSlackConnections, setIsLoadingSlackConnections] =
    useState(false)
  const [isLoadingGmailConnections, setIsLoadingGmailConnections] =
    useState(false)
  const [isLoadingCalendarConnections, setIsLoadingCalendarConnections] =
    useState(false)
  const [loadingUserConnections, setLoadingUserConnections] = useState(false)
  const { data: session, status } = useSession()

  const checkAllUserConnections = useCallback(async () => {
    if (!session?.user?.email) {
      console.log("UserContext: No session, skipping user connections fetch")
      return
    }

    setIsLoadingSlackConnections(true)
    setIsLoadingGmailConnections(true)
    setIsLoadingCalendarConnections(true)

    try {
      const isConnectedToSlack = await getUserSlackIntegrations(
        session.user.email
      )
      const isConnectedToGmail = await checkGmailConnection(session.user.email)
      const isConnectedToCalendar = await checkCalendarConnection(
        session.user.email
      )
      setIsConnectedToSlack(isConnectedToSlack.length > 0)
      setIsConnectedToGmail(isConnectedToGmail)
      setIsConnectedToCalendar(isConnectedToCalendar)
    } catch (error) {
      console.error("Failed to fetch user connections:", error)
    } finally {
      setIsLoadingSlackConnections(false)
      setIsLoadingGmailConnections(false)
      setIsLoadingCalendarConnections(false)
    }
  }, [session?.user])

  const checkUserSlackConnection = useCallback(async () => {
    if (!session?.user?.email) {
      console.log("UserContext: No session, skipping Slack connection fetch")
      return
    }

    setIsLoadingSlackConnections(true)

    try {
      const isConnectedToSlack = await getUserSlackIntegrations(
        session.user.email
      )
      setIsConnectedToSlack(isConnectedToSlack.length > 0)
    } catch (error) {
      console.error(
        `${session?.user?.email}: Failed to fetch user Slack connection:`,
        error
      )
    } finally {
      setIsLoadingSlackConnections(false)
    }
  }, [session?.user])

  const checkUserGmailConnection = useCallback(async () => {
    if (!session?.user?.email) {
      console.log("UserContext: No session, skipping Gmail connection fetch")
      return
    }

    setIsLoadingGmailConnections(true)
    try {
      const isConnectedToGmail = await checkGmailConnection(session.user.email)
      setIsConnectedToGmail(isConnectedToGmail)
    } catch (error) {
      console.error(
        `${session?.user?.email}: Failed to fetch user Gmail connection:`,
        error
      )
    } finally {
      setIsLoadingGmailConnections(false)
    }
  }, [session?.user])

  const checkUserCalendarConnection = useCallback(async () => {
    if (!session?.user?.email) {
      console.log("UserContext: No session, skipping Calendar connection fetch")
      return
    }

    setIsLoadingCalendarConnections(true)
    try {
      const isConnectedToCalendar = await checkCalendarConnection(
        session.user.email
      )
      setIsConnectedToCalendar(isConnectedToCalendar)
    } catch (error) {
      console.error(
        `${session?.user?.email}: Failed to fetch user Calendar connection:`,
        error
      )
    } finally {
      setIsLoadingCalendarConnections(false)
    }
  }, [session?.user])

  useEffect(() => {
    // Only run when session is available
    if (status === "loading") return
    if (status === "unauthenticated") return

    if (session?.user) {
      // Defer connection checks until after initial render to improve page load performance
      // Use requestIdleCallback if available, otherwise fallback to setTimeout
      const deferConnectionChecks = () => {
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          requestIdleCallback(
            () => {
              checkAllUserConnections()
            },
            { timeout: 2000 }
          ) // Fallback after 2 seconds if browser is busy
        } else {
          // Fallback for browsers without requestIdleCallback
          setTimeout(() => {
            checkAllUserConnections()
          }, 100) // Small delay to allow initial render
        }
      }
      deferConnectionChecks()
    }
  }, [session, status, checkAllUserConnections])

  useEffect(() => {
    setLoadingUserConnections(
      isLoadingSlackConnections ||
        isLoadingGmailConnections ||
        isLoadingCalendarConnections
    )
  }, [
    isLoadingSlackConnections,
    isLoadingGmailConnections,
    isLoadingCalendarConnections,
  ])

  return (
    <GlobalContext.Provider
      value={{
        isConnectedToSlack,
        isConnectedToGmail,
        isConnectedToCalendar,
        checkUserSlackConnection,
        checkUserGmailConnection,
        checkUserCalendarConnection,
        isLoadingSlackConnections,
        isLoadingGmailConnections,
        isLoadingCalendarConnections,
        checkAllUserConnections,
        loadingUserConnections,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

// Custom hook for accessing the global context
export const useUserContext = () => useContext(GlobalContext)
